import React, { useState, useEffect } from "react";
import { Typography, Box, Tab, Tabs } from '@mui/material';
import axios from "axios";
import { createTheme, ThemeProvider } from '@mui/material/styles';
import GeneralInformationBox from "../../Utilities/NCProForms/GeneralInformationBox";
import AffectedItems from "../../Utilities/NCProForms/AffectedItems";
import NonConformanceDescription from "../../Utilities/NCProForms/NonConformanceDescription";
import AdditionalInformation from "../../Utilities/NCProForms/AdditionalInformation";
import NDRCEntry from "../../Utilities/NCProForms/NDRCEntry";
import PRCEntry from "../../Utilities/NCProForms/PRCEntry";
import SRCEntry from "../../Utilities/NCProForms/SRCEntry";
import PreventiveActions from "../../Utilities/NCProForms/PreventiveActions";
import Attachments from "../../Utilities/NCProForms/Attachments";
import CorrectiveActionReview from "../../Utilities/NCProForms/CorrectiveActionReview";
import PreventiveActionReview from "../../Utilities/NCProForms/PreventiveActionReview";
import ClosureVerification from "../../Utilities/NCProForms/ClosureVerification";
import { useNavigate } from 'react-router-dom';
import IICA from "../../Utilities/NCProForms/IICA";
import ProgressStepper from "../../Utilities/ProgressStepper";
import CircularLoader from "../../Common/CircularLoader";
import { getProgressStepperStatus } from "../../../Helpers/NCRHelpers";
import { WhyWhyDiagramViewOnlyMode } from "./WhyWhyDiagramViewOnlyMode";
import { FishBoneDiagramViewOnlyMode } from "./FishBoneDiagramViewOnlyMode";

const theme = createTheme({
  components: {
    MuiSelect: {
      styleOverrides: {
        select: {
          fontSize: "14px",
          fontWeight: 400,
          color: "rgba(84, 84, 84, 1)",
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          "&::placeholder": {
            fontSize: "14px",
            fontWeight: 400,
            color: "rgba(84, 84, 84, 1)",
            opacity: 1,
          },
        },
      },
    },
  },
});

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

export default function ViewComponent({ content }) {
  const [formData, setFormData] = useState([]);
  const [tabValue, setTabValue] = useState(0);
  const [inSideTabValue, setInSideTabValue] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const getFromData = async () => {
    try {
      const accessToken = localStorage.getItem('NCProAccessToken');
      setIsLoading(true);
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_BASE_URL}/ncr/forms/${content.ncrId}`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      setFormData(response.data.ncrForm);
    } catch (error) {
      console.error('Error fetching ncr:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const insideTabs = [
    formData?.fb_diagram && "Fish-bone",
    formData?.why_why_analysis && "Why-Why",
  ].filter(Boolean);

  useEffect(() => {
    if (insideTabs.length > 0) {
      setInSideTabValue(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  useEffect(() => {
    getFromData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {isLoading ? (
        <CircularLoader label="NCRDetails" />
      ) : (
        <ThemeProvider theme={theme}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 14 }}>
            <Box sx={{ display: 'flex' }}>
              <Box sx={{ pr: '4px' }}>
                <img
                  src="/images/leftArrow.png"
                  alt="Icon"
                  style={{ width: 24, height: 24, marginRight: 4, cursor: 'pointer' }}
                  onClick={() => navigate('/ncpro/view')}
                />
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontFamily: 'Lato',
                    fontSize: '18px',
                    fontWeight: 700,
                  }}
                >
                  {formData.ncr_id} - View
                </Typography>
              </Box>
            </Box>
            <Box>
              <ProgressStepper
                status={getProgressStepperStatus(formData.status, formData.corrective_action_required)}
              />
            </Box>
          </Box>
          <Box
            sx={{
              height: '75vh',
              overflow: 'auto',
              border: '1px solid #ccc',
              borderRadius: '8px',
              backgroundColor: '#fff',
              marginTop: '16px',
              '&::-webkit-scrollbar': {
                width: '7px',
                height: '7px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(211, 211, 211, 1)',
                borderRadius: '5px',
              }
            }}
          >
            <Box sx={{ width: '100%' }}>
              <Box sx={{
                position: 'sticky',
                top: 0,
                backgroundColor: 'rgba(255, 255, 255, 1)',
                zIndex: 10,
                borderBottom: 1,
                borderColor: 'divider',
              }}>
                <Tabs value={tabValue} onChange={handleTabChange} aria-label="basic tabs example">
                  <Tab
                    label="General information"
                    sx={{
                      fontFamily: 'Lato, sans-serif',
                      fontSize: '14px',
                      fontWeight: tabValue === 0 ? '700' : '400',
                      color: tabValue === 0 ? 'rgba(42, 61, 189, 1) !Important' : 'rgba(0, 0, 0, 1) !Important',
                      lineHeight: '16.8px',
                      textTransform: 'none',
                      py: "20px"
                    }}
                  />
                  <Tab
                    label={formData.corrective_action_required === 'No' ? 'Containment action' : 'Containment action & Root cause'}
                    sx={{
                      fontFamily: 'Lato, sans-serif',
                      fontSize: '14px',
                      fontWeight: tabValue === 1 ? '700' : '400',
                      color: tabValue === 1 ? 'rgba(42, 61, 189, 1) !Important' : 'rgba(0, 0, 0, 1) !Important',
                      lineHeight: '16.8px',
                      textTransform: 'none',
                      py: "20px"
                    }}
                  />
                  {insideTabs.length > 0 &&
                    <Tab
                      label='Root Cause Analysis'
                      sx={{
                        fontFamily: 'Lato, sans-serif',
                        fontSize: '14px',
                        fontWeight: tabValue === 2 ? '700' : '400',
                        color: tabValue === 2 ? 'rgba(42, 61, 189, 1) !Important' : 'rgba(0, 0, 0, 1) !Important',
                        lineHeight: '16.8px',
                        textTransform: 'none',
                        py: "20px"
                      }}
                    />
                  }
                </Tabs>
              </Box>
              <CustomTabPanel value={tabValue} index={0}>
                <GeneralInformationBox content={{ formData: formData, inputDisable: true }} />
                <AffectedItems content={{ formData: formData, inputDisable: true }} />
                <NonConformanceDescription content={{ formData: formData, inputDisable: true }} />
                <AdditionalInformation content={{ label: "view", formData: formData, inputDisable: true }} />
              </CustomTabPanel>
              <CustomTabPanel value={tabValue} index={1}>
                <IICA content={{ formData: formData, inputDisable: true }} />
                <NDRCEntry content={{ formData: formData, inputDisable: true }} />
                <PRCEntry content={{ formData: formData, inputDisable: true }} />
                <SRCEntry content={{ formData: formData, inputDisable: true }} />
                <PreventiveActions content={{ formData: formData, inputDisable: true }} />
                <Attachments content={{ formData: formData, inputDisable: true }} />
                <CorrectiveActionReview content={{ formData: formData, inputDisable: true }} />
                <PreventiveActionReview content={{ formData: formData, inputDisable: true }} />
                <ClosureVerification content={{ formData: formData, inputDisable: true }} />
              </CustomTabPanel>
              {insideTabs.length > 0 &&
                <CustomTabPanel value={tabValue} index={2}>
                  <Box sx={{
                    position: 'relative',
                    margin: "40px",
                    border: "2px solid rgba(128, 128, 128, 0.2)",
                    borderRadius: "8px",
                  }}>
                    <Box sx={{
                      position: 'sticky',
                      top: "58px",
                      backgroundColor: 'rgba(255, 255, 255, 1)',
                      zIndex: 5,
                    }} className='fishBoneClass'>
                      <Tabs
                        value={inSideTabValue}
                        onChange={(e, value) => setInSideTabValue(value)}
                        aria-label="basic tabs example"
                        sx={{
                          width: '100%',
                          display: 'flex',
                          justifyContent: "space-around",
                          borderBottom: '2px solid rgba(128, 128, 128, 0.2)',
                        }}
                        TabIndicatorProps={{
                          style: {
                            height: 3,
                            backgroundColor: '#2A3DBD',
                          },
                        }}
                      >
                        {insideTabs.map((tab, i) => (
                          <Tab
                            key={tab}
                            label={`${tab} Analysis`}
                            sx={{
                              flex: 1,
                              fontFamily: 'Lato, sans-serif',
                              fontSize: '14px',
                              fontWeight: inSideTabValue === i ? '700' : '400',
                              color: inSideTabValue === i ? 'rgba(42, 61, 189, 1)' : 'rgba(0, 0, 0, 1)',
                              lineHeight: '16.8px',
                              textTransform: 'none',
                              py: '20px',
                            }}
                          />
                        ))}
                      </Tabs>
                    </Box>
                    {insideTabs.map((tab, i) => (
                      <CustomTabPanel key={tab} value={inSideTabValue} index={i}>
                        {tab === 'Fish-bone' && <FishBoneDiagramViewOnlyMode ncrData={formData} />}
                        {tab === 'Why-Why' && <WhyWhyDiagramViewOnlyMode ncrData={formData} />}
                      </CustomTabPanel>
                    ))}
                  </Box>
                </CustomTabPanel>
              }
            </Box>
          </Box>
        </ThemeProvider>
      )}
    </>
  );
}
